import isUndefined from 'lodash/isUndefined';
const {readCookie, TRIP_COOKIE} = require('../../universal/utils/cookieHelpers');
const {isValidDate, areBothDatesValid, isBefore, isPast, formatDate} = require('./dateHelper');
import {getQueryParams} from '../utils/queryStringHelper';
import {setQueryStringParameter} from '../utils/history';
import {log as logToServer} from '../api/logger';

// Return a value is it is true or false, but not if it is undefined
function isTrueOrFalse(val) {
    return val === 'true' || val === 'false' || val === true || val === false;
}

// Because isNaN(true) is false
// We need to add additional checks to get only ints and int strings
function isIntOrIntString(value) {
    return +value === parseInt(value, 10);
}

// Why a dictionary instead of an array?  Because O(1) < 0(n)
const queryParamKeys = {
    // key: 'preferredKey',
    locale: 'locale',
    siteName: 'siteName',
    site: 'site',
    currency: 'currency',
    r: 'r',
    cid: 'cid',
    arrival: 'arrival',
    arrivalDate: 'arrival',
    arrive: 'arrival',
    arriveDate: 'arrival',
    departure: 'departure',
    departureDate: 'departure',
    depart: 'departure',
    departDate: 'departure',
    numberOfAdults: 'adultsCount',
    adultsCount: 'adultsCount',
    adults: 'adultsCount',
    guests: 'adultsCount',
    numberOfChildren: 'childrenCount',
    childrenCount: 'childrenCount',
    children: 'childrenCount',
    numberOfInfants: 'infantsCount',
    infantsCount: 'infantsCount',
    infants: 'infantsCount',
    petIncluded: 'petIncluded',
    petsIncluded: 'petIncluded',
    pet: 'petIncluded',
    pets: 'petIncluded',
    noDates: 'noDates',
    noGuests: 'noGuests'
};

/* istanbul ignore next */
function logUnrecognizedParams(query, logFn) {
    Object.keys(query).forEach((key) => {
        if (!queryParamKeys[key]) {
            const value = query[key];
            logFn({
                queryParam: key,
                used: false,
                processed: false,
                value
            });
        }
    });
}

function getValidQueryField(query, preferredkey, logFn, validFn) {
    // Check all permutations of adults, adultsCount, etc in query
    // And return the first value that meets validation requirements
    // Delete all but preferred param name from query string
    // Log all param names to Splunk
    const keyArray = Object.keys(queryParamKeys).filter((x) => queryParamKeys[x] === preferredkey);
    const keysUsedInQueryArray = keyArray.filter((key) => !isUndefined(query[key]));
    const keyToUse = keysUsedInQueryArray.find((key) => validFn(query[key]));
    const valueToUse = query[keyToUse];
    const preferredKey = queryParamKeys[keyToUse];

    keysUsedInQueryArray.forEach((key) => {
        const currValue = query[key];
        const isPreferredKey = key === preferredKey;
        const useKeyValue = key === keyToUse;

        if (!isPreferredKey) {
            setQueryStringParameter(key, null);
        }

        logFn({
            queryParam: key,
            preferredParam: !isPreferredKey ? preferredKey : null,
            used: useKeyValue,
            valid: validFn(currValue),
            value: currValue
        });
    });

    setQueryStringParameter(preferredKey, valueToUse);

    return valueToUse;
}

function bothDatesExist(arriveDate, departDate) {
    return !!arriveDate && !!departDate;
}

function qualifyDates(arriveDate, departDate) {
    if (!bothDatesExist(arriveDate, departDate)) {
        return null;
    }
    if (!areBothDatesValid(arriveDate, departDate)) {
        return null;
    }
    if (!isBefore(arriveDate, departDate) || isPast(arriveDate)) {
        return {
            arriveDate: '',
            departDate: ''
        };
    }
    return {
        arriveDate: formatDate(arriveDate),
        departDate: formatDate(departDate)
    };
}

/* eslint-disable complexity */
function determineTripDates(query, tripCookie, stateCookie, logFn) {
    logUnrecognizedParams(query, logFn);

    const dateValidFn = (value) => isValidDate(new Date(value));
    const qArrival = getValidQueryField(query, 'arrival', logFn, dateValidFn);
    const qDeparture = getValidQueryField(query, 'departure', logFn, dateValidFn);

    const datesFromQuery = qualifyDates(qArrival, qDeparture);
    const emptyDates = {
        source: 'none', // Used for debugging in store.js
        arriveDate: '',
        departDate: ''
    };

    if (query.noDates === 'true' && !datesFromQuery) {
        return emptyDates;
    }

    if (datesFromQuery) {
        datesFromQuery.source = 'queryParam'; // Used for debugging in store.js
        return datesFromQuery;
    }

    let datesFromTripCookie = null;
    if (tripCookie.arrival && tripCookie.departure) {
        const tArrival = tripCookie.arrival;
        const tDeparture = tripCookie.departure;
        datesFromTripCookie = qualifyDates(tArrival, tDeparture);
    }

    if (datesFromTripCookie) {
        datesFromTripCookie.source = 'tripCookie'; // Used for debugging in store.js
        return datesFromTripCookie;
    }

    let datesFromStateCookie = null;
    if (stateCookie.previousArrivalDate && stateCookie.previousDepartureDate) {
        const sArrival = formatDate(decodeURIComponent(stateCookie.previousArrivalDate));
        const sDeparture = formatDate(decodeURIComponent(stateCookie.previousDepartureDate));
        datesFromStateCookie = qualifyDates(sArrival, sDeparture);
    }

    if (datesFromStateCookie) {
        datesFromStateCookie.source = 'stayCookie'; // Used for debugging in store.js
        return datesFromStateCookie;
    }

    return emptyDates;
}

/* eslint-enable complexity */

function getIsFamilySegment(hasKids, stateCookie) {
    const isFamilyFilterSet =
        stateCookie &&
        stateCookie.lastSearchUrl &&
        stateCookie.lastSearchUrl.indexOf('filter:43') > 0;
    return hasKids || isFamilyFilterSet;
}

/* eslint-disable complexity */
function determineTripData(haTripPrst, haStatePrst, query, logFn) {
    const tripDates = determineTripDates(query, haTripPrst, haStatePrst, logFn);

    // Get the first value that meets the validation requirements for each param type
    // Can be the preferred param name (eg. adultsCount) or nonpreferred name (eg. adults)
    const humansValidFn = (value) => isIntOrIntString(value);
    const petsValidFn = (value) => isTrueOrFalse(value);

    const qNumberOfAdults = getValidQueryField(query, 'adultsCount', logFn, humansValidFn);
    let qNumberOfChildren = getValidQueryField(query, 'childrenCount', logFn, humansValidFn);
    let qNumberOfInfants = getValidQueryField(query, 'infantsCount', logFn, humansValidFn);
    let qPets = getValidQueryField(query, 'petIncluded', logFn, petsValidFn);

    // If query includes adults but not children, default children to 0
    if (qNumberOfAdults > 0) {
        qNumberOfChildren = qNumberOfChildren || 0;
        qNumberOfInfants = qNumberOfInfants || 0;
        qPets = qPets || false;
    }

    // Use value from query if defined; otherwise, use value from cookie
    const safeParseAsNumber = (input) => +input || 0;

    let adultsCount = safeParseAsNumber(
        isIntOrIntString(qNumberOfAdults) ? qNumberOfAdults : haTripPrst.numberOfAdults
    );
    let childrenCount = safeParseAsNumber(
        isIntOrIntString(qNumberOfChildren) ? qNumberOfChildren : haTripPrst.numberOfChildren
    );
    let infantsCount = safeParseAsNumber(
        isIntOrIntString(qNumberOfInfants) ? qNumberOfInfants : haTripPrst.numberOfInfants
    );
    let hasPets = typeof qPets === 'undefined' ? haTripPrst.petIncluded || false : qPets;

    // At this point, hasPets could be a bool or a string; if it is a string, convert it
    hasPets = [true, 'true'].indexOf(hasPets) > -1 ? true : false;

    if (query.noGuests === 'true') {
        adultsCount = 0;
        childrenCount = 0;
        infantsCount = 0;
    }

    const isFamilySegment = getIsFamilySegment(childrenCount > 0 || infantsCount > 0, haStatePrst);

    return {
        ...tripDates,
        adultsCount,
        childrenCount,
        infantsCount,
        hasPets,
        isFamilySegment
    };
}

/* eslint-enable complexity */

function determineTripDataFromRequest(request) {
    const haTripPrst = readCookie(TRIP_COOKIE, request.headers.cookie);
    const haStatePrst = readCookie('ha-state-prst', request.headers.cookie);
    const query = request.query || {};
    const logFn = () => {
        // don't log anything when processing query params on the backend
    };

    return determineTripData(haTripPrst, haStatePrst, query, logFn);
}

function determineTripDataFromWindow(window) {
    const haTripPrst = readCookie(TRIP_COOKIE, window.document.cookie);
    const haStatePrst = readCookie('ha-state-prst', window.document.cookie);
    const query = getQueryParams(window.location.search);
    const logFn = (data) => {
        data.fromClient = true;
        logToServer(data);
    };

    return determineTripData(haTripPrst, haStatePrst, query, logFn);
}

export {
    determineTripData,
    determineTripDataFromRequest,
    determineTripDataFromWindow,
    getValidQueryField,
    isIntOrIntString
};
